/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import {
	URL_TRANSACTION_PROD,
	URL_TRANSACTION_STAGING,
} from '@config/constants/blockchain';
import { NOT_APPLICABLE } from '@config/constants/common';
import { IS_PRODUCTION } from '@config/constants/env';
import { paths } from '@config/constants/paths';
import { ROLES } from '@config/constants/roles';
import i18n from '@config/i18n/config';
import { TTechnicalDocument } from '@features/catalogs/meausrability-projects/list/types';
import { TMesurabilityProjectQuaryParams } from '@features/offsets/types';
import {
	TTargetColumn,
	TUser,
} from '@global-types/index';
import dayjs from 'dayjs';
import Decimal from 'decimal.js';
import exportFromJSON from 'export-from-json';
import isEqual from 'lodash/isEqual';
import map from 'lodash/map';
import sortBy from 'lodash/sortBy';
import { matchPath } from 'react-router';
import { formatNumberWithSpaces } from './formats';
import { getUnitLabel } from './units';

/* -------------------------------------------------------------------------- */
/*                                Functions                                   */
/* -------------------------------------------------------------------------- */

export const getMintStatusType = (mintStatus: string) => {
	switch (mintStatus) {
		case 'AVAILABLE':
			return '';
		case 'MINTED':
			return 'success';
		case 'LOCKED':
			return 'warning';
		case 'SUCCESS':
			return 'success';
		case 'DONE':
			return 'success';
		case 'BYPASSED':
			return 'success';
		case 'ROLLED_BACK':
			return 'warning';
		case 'IN_PROGRESS':
			return 'warning';
		case 'PENDING':
			return 'warning';
		case 'FAILED':
			return 'error';
		case 'DESTROYED':
			return 'error';
		case 'ERROR':
			return 'error';

		default:
			return '';
	}
};

export const getMintStatusLabel = (
	mintStatus: string
) => {
	switch (mintStatus) {
		case 'AVAILABLE':
			return 'Available';
		case 'MINTED':
			return 'Minted';
		case 'DESTROYED':
			return 'Destroyed';
		case 'DONE':
			return 'Done';
		case 'LOCKED':
			return 'Locked';
		case 'IN_PROGRESS':
			return 'In progress';
		case 'FAILED':
			return 'Failed';
		case 'ERROR':
			return 'Error';
		case 'PENDING':
			return 'Pending';
		case 'SUCCESS':
			return 'Success';
		case 'BYPASSED':
			return 'Bypassed';
		case 'ROLLED_BACK':
			return 'RolledBack';
		default:
			return 'Failed';
	}
};

export const cleanObject = (obj: any) => {
	if (typeof obj === 'object')
		for (const propName in obj) {
			if (
				obj[propName] === null ||
				obj[propName] === '' ||
				obj[propName] === undefined
			) {
				delete obj[propName];
			}
		}
	return obj;
};

export const removeEmptyArrays = (
	obj: TMesurabilityProjectQuaryParams
) => {
	return Object.fromEntries(
		Object.entries(obj).filter(
			([, value]) =>
				!Array.isArray(value) || value.length > 0
		)
	);
};

export const cloneDeep = (originalValue: any) => {
	try {
		const cloneValue = JSON.parse(
			JSON.stringify(originalValue, null, 2)
		);
		return cloneValue;
	} catch {
		return originalValue;
	}
};

/* -------------------------------------------------------------------------- */
/*                                  Date                                      */
/* -------------------------------------------------------------------------- */

export const formatDate = (
	date: number | string | Date | dayjs.Dayjs,
	format: string
) => dayjs(date).format(format);

/* -------------------------------------------------------------------------- */
/*                                  Strings                                   */
/* -------------------------------------------------------------------------- */

export function calculateSum(array: string[]) {
	return array.reduce((sum: number, element: any) => {
		const number = parseFloat(
			element.replace(' tCO2e', '').replace(/\s/g, '')
		);
		return sum + (isNaN(number) ? 0 : number);
	}, 0);
}

export function capitalizeString(input: any) {
	return (
		input.charAt(0).toUpperCase() +
		input.slice(1).toLowerCase()
	);
}

interface TruncateOptions {
	separator?: string;
	omission?: string;
}

export function truncateString(
	input: string,
	maxLength: number,
	options: TruncateOptions = {}
): string {
	const { separator = '...', omission = separator } =
		options;

	if (input == null || typeof input !== 'string') {
		return NOT_APPLICABLE;
	}

	if (input.length <= maxLength) {
		return input;
	}

	const truncatedLength = maxLength - omission.length;
	let truncatedString = input.slice(0, truncatedLength);

	if (separator) {
		const lastSeparatorIndex =
			truncatedString.lastIndexOf(separator);
		if (lastSeparatorIndex !== -1) {
			truncatedString =
				truncatedString.slice(0, lastSeparatorIndex) +
				omission;
		} else {
			truncatedString += omission;
		}
	} else {
		truncatedString += omission;
	}

	return truncatedString;
}

export const isEmptyObject = (
	obj: Record<string, any>
): boolean => {
	return Object.keys(obj).length === 0;
};

/* -------------------------------------------------------------------------- */
/*                                  UUID                                      */
/* -------------------------------------------------------------------------- */

export function uuid() {
	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
		/[xy]/g,
		function (c) {
			const r = (Math.random() * 16) | 0,
				v = c === 'x' ? r : (r & 0x3) | 0x8;
			return v.toString(16);
		}
	);
}

/* -------------------------------------------------------------------------- */
/*                                  Options                                   */
/* -------------------------------------------------------------------------- */

export function convertArrayToLabelValue(array: any) {
	if (array && Array.isArray(array)) {
		return array.map((item: any) => ({
			label: item.name,
			value: item.id,
		}));
	} else {
		return [];
	}
}

export function convertUsersToLabelValue(array: TUser) {
	if (array && Array.isArray(array)) {
		return array.map((item: TUser) => ({
			label: item.firstName + ' ' + item.lastName,
			value: item.id,
		}));
	} else {
		return [];
	}
}

export function convertArrayMeterPointsToLabelValue(
	array: any
) {
	return array.map((item: any) => ({
		label: item.serialNumber,
		value: item.id,
	}));
}

/* -------------------------------------------------------------------------- */
/*                                  Numeric                                   */
/* -------------------------------------------------------------------------- */

export function convertProduction(
	production: number,
	unit: string
) {
	switch (unit.toLocaleLowerCase()) {
		case 'item':
			return production + ' item';
		case 'kwh':
			return convertKWHToMWH(production, unit);
		case 'kg':
			return convertKGToT(production);
		default:
			return 'null';
	}
}

export function getRatioLabel(
	value: number,
	unit: string
) {
	if (isNaN(value)) {
		return NOT_APPLICABLE;
	}

	let formattedValue = value.toFixed(3);

	if (/\.?0+$/.test(formattedValue)) {
		formattedValue =
			parseFloat(formattedValue).toString();
	}

	if (unit === 'KWH') {
		return `${formattedValue} ${getUnitLabel(
			'gCO2perKwh'
		)}`;
	} else if (unit === 'KG') {
		return `${formattedValue} ${getUnitLabel(
			'gCO2perKg'
		)}`;
	} else {
		return `${formattedValue}`;
	}
}

export function getRatioUnit(unit: string) {
	if (unit === 'KWH') {
		return getUnitLabel('gCO2perKwh');
	} else if (unit === 'KG') {
		return getUnitLabel('gCO2perKg');
	} else {
		return ``;
	}
}

export const getUnitFromProjectUnit = (_unit: string) => {
	switch (_unit) {
		case 'MWH':
			return getUnitLabel('mwh');
		case 'KWH':
			return getUnitLabel('mwh');
		case 't':
			return getUnitLabel('t');
		case 'T':
			return getUnitLabel('t');
		case 'kg':
			return getUnitLabel('t');
		case 'KG':
			return getUnitLabel('t');
		default:
			break;
	}
};

export function convertMWHToKWHValue(mwh: number) {
	const val = new Decimal(mwh);
	const kwh = val.mul(1000);
	return kwh.toNumber();
}

export function convertKWHToMWHValue(kwh: number) {
	const val = new Decimal(kwh);
	const mwh = val.div(1000);
	return mwh.toNumber();
}

export function convertTToKgValue(t: number) {
	const val = new Decimal(t);
	const kwh = val.mul(1000);
	return kwh.toNumber();
}

export function convertKWHToMWH(
	kwh: number,
	unit: string
) {
	if (unit !== 'KWH') {
		return 'null';
	}
	const mwh = kwh / 1000;
	return `${formatNumberWithSpaces(Number(mwh.toFixed(3)))} ${getUnitLabel('mwh')}`;
}

export function convertMWHToKWH(
	mwh: number,
	unit: string
) {
	if (unit !== 'MWH') {
		return 'null';
	}
	const kwh = mwh * 1000;
	return `${kwh.toFixed(3)} ${getUnitLabel('kwh')}`;
}

export function convertGCO2EToTCO2EWithoutUnit(
	_gco2e: number
) {
	const gco2e = new Decimal(_gco2e);
	const tco2e = gco2e.div(1000000);
	return tco2e.toNumber();
}

export function convertTCO2EToGCO2EWithoutUnit(
	_tco2e: number
) {
	const tco2e = new Decimal(_tco2e);
	const gco2e = tco2e.mul(1000000);
	return gco2e.toNumber();
}

export function convertGCO2EToTCO2EWithoutUnitFloor(
	_gco2e: number
) {
	const gco2e = new Decimal(_gco2e);
	const tco2e = gco2e.div(1000000);
	return Math.floor(tco2e.toNumber());
}

export function convertGCO2EToTCO2E(gco2e: number) {
	const tco2e = gco2e / 1000000;
	return `${formatNumberWithSpaces(Number(tco2e.toFixed(2)))} ${getUnitLabel('tco2e')}`;
}

export function convertTCO2EToGCO2E(tco2e: number) {
	const gco2e = tco2e * 1000000;
	return `${formatNumberWithSpaces(Number(gco2e.toFixed(3)))} ${getUnitLabel('gco2e')}`;
}

export function convertTToKG(t: number) {
	const kg = t * 1000;
	return `${formatNumberWithSpaces(Number(kg.toFixed(3)))} ${getUnitLabel('kg')}`;
}

export function convertKGToT(kg: number) {
	const t = kg / 1000;
	return `${formatNumberWithSpaces(Number(t.toFixed(3)))} ${getUnitLabel('t')}`;
}

export function addZeroToTenPlace(_number: number) {
	if (_number < 10) {
		const numberWithZero = '0' + _number;
		return numberWithZero;
	} else {
		return _number;
	}
}

export const parseTokenEquivalentToTCO2E = (
	_convertionFactor: any,
	_quantity: number
) => {
	if (_convertionFactor == 'null') {
		return '0 ' + getUnitLabel('tco2e');
	}
	return (
		formatNumberWithSpaces(
			Number(
				(
					Math.round(_convertionFactor * _quantity) /
					1000000
				).toFixed(3)
			)
		) +
		' ' +
		getUnitLabel('tco2e')
	);
};

export const parseTokenEquivalent = (
	_fcApplicable: any,
	_quantity: number
) => {
	if (_fcApplicable == 'null') {
		return '0 ' + getUnitLabel('tco2e');
	}
	return (
		Math.round(_fcApplicable * _quantity) +
		' ' +
		getUnitLabel('tco2e')
	);
};

export const getConvertionFactor = (
	_conversionFactorOverride: number,
	_conversionFactor: {
		id: string;
		ratio: number;
		unit: string;
	}
) => {
	if (!_conversionFactorOverride) {
		return _conversionFactor
			? formatNumberWithSpaces(
					Number(_conversionFactor.ratio)
				)
			: 'null';
	} else {
		// eslint-disable-next-line no-extra-boolean-cast
		if (!!_conversionFactorOverride)
			return formatNumberWithSpaces(
				Number(_conversionFactorOverride)
			);
	}
};

export const parseFcApplicable = (
	_conversionFactorOverride: any,
	_conversionFactor: any,
	_unit: string
) => {
	const unitLabels: Record<string, string> = {
		kwh: getUnitLabel('gCO2perKwh'),
		kg: getUnitLabel('gCO2perKg'),
		item: getUnitLabel('gCO2perItem'),
	};

	const unit =
		unitLabels[_unit.toLowerCase()] || NOT_APPLICABLE;

	if (!_conversionFactorOverride) {
		return _conversionFactor
			? `${formatNumberWithSpaces(Number(_conversionFactor.ratio))} ${unit}`
			: 'null';
	} else {
		// eslint-disable-next-line no-extra-boolean-cast
		if (!!_conversionFactorOverride)
			return `${formatNumberWithSpaces(Number(_conversionFactorOverride))} ${unit}`;
	}
};

export const calculate40Percent = (_number: number) => {
	if (typeof _number !== 'number') {
		throw new Error('Input must be a number');
	}

	return (_number * 0.4).toFixed(0);
};

export const formatDigit = (_value: number) => {
	return _value < 10 ? `0${_value}` : `${_value}`;
};

export function checkArrayContainsString(obj: any) {
	return (
		typeof obj === 'object' &&
		obj !== null &&
		'project' in obj &&
		Array.isArray(obj.project) &&
		obj.project.some(
			(item: any) => typeof item === 'string'
		)
	);
}

/* -------------------------------------------------------------------------- */
/*                                  Exports CSV                               */
/* -------------------------------------------------------------------------- */

/* ******************************** CONFIG ******************************* */

const exportType = exportFromJSON.types.csv;

const allFields = [
	{
		field: 'project.name',
		text: 'Project',
	},
	{
		field: 'site.name',
		text: 'Site',
	},
	{
		field: 'startDate',
		text: 'Start date',
	},
	{
		field: 'quantity',
		text: 'Production',
	},
	{
		field: 'verified',
		text: 'Verified',
	},
];

/* ******************************** HELPER ******************************* */
export function convertContentToJSON(
	content: string
): any {
	const csvText = content;
	const rows = csvText.split('\n');

	const oldHeaders = rows[0].split(';');

	const headers = oldHeaders.map((originalValue) => {
		const fieldWithoutQuotes = originalValue.replace(
			/"/g,
			''
		);
		const matchingField = allFields.find(
			(field) => field.field === fieldWithoutQuotes
		);
		return matchingField
			? matchingField.text
			: originalValue;
	});

	const jsonData = rows.slice(1).map((row) => {
		const values = row.split(';');
		const obj: any = {};
		headers.forEach((header, index) => {
			obj[header] = values[index].replace(/"/g, '');
		});
		return obj;
	});
	return jsonData;
}

export function exportCSV(
	body: string,
	fileName: string
) {
	exportFromJSON({
		data: JSON.parse(JSON.stringify(body, null, 2)),
		fileName,
		exportType,
		delimiter: ';',
	});
}

export const extractColumnNames = (
	csvContent: string
): string[] => {
	const separator = detectSeparator(csvContent);
	if (separator) {
		return csvContent.split('\n')[0].split(separator);
	} else {
		return [];
	}
};

export const detectSeparator = (csv_content: string) => {
	let csv_separator = null;
	const possibleSeparators = [',', ';', ' ', '\t'];
	const lines = csv_content.trim().split(/\r?\n/);
	for (const separator of possibleSeparators) {
		const counts = lines
			.slice(0, 3)
			.map((line) => line.split(separator).length);
		const uniqueCounts = [...new Set(counts)];
		// If there is only one unique count, it might be a candidate separator
		if (uniqueCounts.length === 1) {
			const [count] = uniqueCounts;
			// Check if the count is reasonable for the number of columns in CSV
			if (count > 1) {
				csv_separator = separator;
				return csv_separator;
			}
		}
	}
};

/**
 * Subtracts 20% from a given number.
 * @param {number} number - The number to subtract from.
 * @returns {number} The result after subtracting 20%.
 */
export const sum20Percent = (_value: number) => {
	const value = new Decimal(_value);
	const percentage = new Decimal(0.2);
	const result = value.add(value.mul(percentage));
	return result.toNumber();
};

export const handleOpenTransactionExternalLink = (
	transactionHash: string
) => {
	window.open(
		IS_PRODUCTION
			? `${URL_TRANSACTION_PROD}/${transactionHash}`
			: `${URL_TRANSACTION_STAGING}/${transactionHash}`,
		'_blank'
	);
};

/**
 * Finds the object with the `startDate` closest to the current date.
 *
 * @param {Array<{ name: string, startDate: string }>} objects - An array of objects where each object must have a `name` (string) and a `startDate` (string in ISO date format).
 * @returns { { name: string, startDate: string } | null } - The object with the `startDate` closest to the current date. Returns `null` if the array is empty.
 *
 */
export const findClosestStartDate = (
	objects: any[]
): any | null => {
	if (objects.length === 0) {
		return NOT_APPLICABLE;
	}

	const now = new Date();

	return objects.reduce((closest, current) => {
		const closestDate = new Date(
			closest.startDate
		).getTime();
		const currentDate = new Date(
			current.startDate
		).getTime();
		const nowTime = now.getTime();

		const closestDifference = Math.abs(
			closestDate - nowTime
		);
		const currentDifference = Math.abs(
			currentDate - nowTime
		);

		return currentDifference < closestDifference
			? current
			: closest;
	}, objects[0]);
};

export const bytesToMB = (bytes: number) => {
	const _bytes = new Decimal(bytes);
	const mb = _bytes.div(1024 * 1024);
	return mb.toNumber().toFixed(0);
};

export const isValidObjectId = (id: string) => {
	const regex = /^[a-fA-F0-9]{24}$/;
	return regex.test(id);
};

export const isNumber = (value: any) => {
	return typeof value === 'number';
};

export const haveSameTechnicalDocumentsNames = (
	currentDocuments: TTechnicalDocument[],
	previousDocuments: TTechnicalDocument[]
) => {
	const currentFileNames = map(
		currentDocuments,
		'filename'
	);
	const previousFileNames = map(
		previousDocuments,
		'filename'
	);

	return isEqual(
		sortBy(currentFileNames),
		sortBy(previousFileNames)
	);
};

export const getVisibleColumns = (
	currentColumns: any[],
	targetColumns: TTargetColumn[]
) => {
	const activeColumns = targetColumns
		.filter((config) => config.checked)
		.map((config) => config.name);

	const visibleColumns = currentColumns.filter((col) =>
		activeColumns.includes(col.accessor)
	);

	return targetColumns
		.filter((config) => config.checked)
		.map((config) => {
			const column = visibleColumns.find(
				(col) => col.accessor === config.name
			);
			return column || {};
		});
};

/**
 * Formats a duration in milliseconds into MM:SS format.
 * @param {number} milliseconds - The duration in milliseconds.
 * @returns {string} The formatted time as MM:SS.
 */
export const formatTime = (milliseconds: number) => {
	const totalSeconds = Math.floor(milliseconds / 1000);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const seconds = totalSeconds % 60;
	return `${minutes
		.toString()
		.padStart(
			2,
			'0'
		)}:${seconds.toString().padStart(2, '0')}`;
};

export const getRoleLabel = (role: string) => {
	switch (role) {
		case ROLES.SUPERADMIN:
			return ROLES.SUPERADMIN;
		case ROLES.ADMIN:
			return ROLES.ADMIN;
		case ROLES.STAFF:
			return ROLES.STAFF;
		case ROLES.USER:
			return ROLES.USER;
		case ROLES.ORGANIZATION_ADMIN:
			return ROLES.ORGANIZATION_ADMIN;
		case ROLES.ORGANIZATION_STAFF:
			return ROLES.ORGANIZATION_STAFF;
		case ROLES.ORGANIZATION_MEMBER:
			return ROLES.ORGANIZATION_MEMBER;
		case ROLES.BRAND_ADMIN:
			return ROLES.BRAND_ADMIN;
		case ROLES.BRAND_STAFF:
			return ROLES.BRAND_STAFF;
		case ROLES.PRODUCER_ADMIN:
			return ROLES.PRODUCER_ADMIN;
		case ROLES.PRODUCER_STAFF:
			return ROLES.PRODUCER_STAFF;
		case ROLES.CLIENT_BRAND:
			return ROLES.CLIENT_BRAND;
		case ROLES.ANONYMOUS:
			return ROLES.ANONYMOUS;
		default:
			return 'Unknown';
	}
};
export function getTitleFromPathname(
	currentPath: string
): string | null {
	const regexDetailMeasurability =
		/^\/measurability-projects\/[0-9a-fA-F]{24}$/;
	const regexDetailAdditionality =
		/^\/additionality-projects\/[0-9a-fA-F]{24}$/;

	if (currentPath === '/measurability-projects') {
		return i18n.t('global.measurabilities');
	}
	if (currentPath === '/additionality-projects') {
		return i18n.t('global.additionalities');
	}
	if (currentPath === '/brands') {
		return i18n.t('global.brands');
	}
	if (currentPath === '/productions') {
		return i18n.t('global.productions');
	}
	if (currentPath === '/producers') {
		return i18n.t('global.producers');
	}
	if (currentPath === '/account-settings') {
		return i18n.t('sidebar.accountSettings');
	}
	if (regexDetailMeasurability.test(currentPath)) {
		return i18n.t('global.detailMeasurabilityProject');
	}

	if (regexDetailAdditionality.test(currentPath)) {
		return i18n.t('global.detailAdditionalityProject');
	}
	function search(
		node: Record<string, any>,
		basePath = ''
	): string | null {
		for (const key in node) {
			const entry = node[key];
			if (entry && typeof entry === 'object') {
				if (
					'path' in entry &&
					typeof entry.path === 'string'
				) {
					const fullPath = entry.path.startsWith('/')
						? entry.path
						: `${basePath}/${entry.path}`.replace(
								/\/+/g,
								'/'
							);
					if (
						matchPath(
							{ path: fullPath, end: true },
							currentPath
						)
					) {
						return entry.title ?? null;
					}
				}
				if (!('path' in entry)) {
					const result = search(entry, basePath);
					if (result) return result;
				}
			}
		}
		return null;
	}
	return search(paths);
}

export function getFundedPercentage(
	pledged?: number,
	target?: number
): number {
	if (!target || target <= 0) return 0;

	return new Decimal(pledged || 0)
		.div(target)
		.mul(100)
		.toDecimalPlaces(2)
		.toNumber();
}

type Variables = Record<string, string | number>;

export function interpolate(
	template: string,
	variables: Variables
): string {
	return template.replace(/{(.*?)}/g, (_, key) => {
		const value = variables[key.trim()];
		return value !== undefined
			? String(value)
			: `{${key}}`;
	});
}

export const formatQueryParams = (
	queryParams?: Record<string, any> | null
): Record<string, any> | undefined => {
	if (!queryParams) return undefined;

	const formatted = { ...queryParams };

	if (formatted.startDate && formatted.startDate.$lt) {
		formatted.startDate = {
			...formatted.startDate,
			$lt: dayjs(formatted.startDate.$lt)
				.add(1, 'day')
				.format('YYYY-MM-DD'),
		};
	}

	if (formatted.createdAt && formatted.createdAt.$lt) {
		formatted.createdAt = {
			...formatted.createdAt,
			$lt: dayjs(formatted.createdAt.$lt)
				.add(1, 'day')
				.format('YYYY-MM-DD'),
		};
	}

	return formatted;
};
